import React from "react";
import { useNavigate, useLocation } from "react-router-dom";
import logo from "../assets/logo.svg";
import { useAuth } from "../contexts/AuthContext";
import toast from "react-hot-toast";
import {
  Home, Building2, Package, UserX, Wrench, MapPin, Shield,
  Users, Calendar, Settings, User, MessageSquare, LogOut,
  ChevronRight, ChevronLeft,
} from "lucide-react";

const SidebarNavigation = ({
  sidebarOpen,
  setSidebarOpen,
  sidebarCollapsed,
  setSidebarCollapsed,
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { logout, hasPermission } = useAuth();

  const currentPage = location.pathname.replace("/", "") || "dashboard";

  const navigationItems = [
    { id: "dashboard",    label: "Dashboard",            icon: Home,          permission: "access_dashboard" },
    { id: "properties",   label: "Properties",           icon: Building2,     permission: "view_building_info" },
    { id: "assets",       label: "Assets",               icon: Package,       permission: "view_asset_details" },
    { id: "visitors",     label: "Visitor Management",   icon: UserX,         permission: "manage_visitors" },
    { id: "service-logs", label: "Service Logs",         icon: Wrench,        permission: "view_asset_details" },
    { id: "geolocation",  label: "Geolocation",          icon: MapPin,        permission: "view_asset_details" },
    { id: "health-safety",label: "Health & Safety",      icon: Shield,        permission: "view_asset_details" },
    { id: "engagement",   label: "Employee Engagement",  icon: Users,         permission: "view_asset_details" },
    { id: "assignments",  label: "Assignment Management",icon: Calendar,      permission: "manage_users" },
    { id: "settings",     label: "Settings",             icon: Settings,      permission: "manage_users" },
    { id: "users",        label: "Users",                icon: User,          permission: "manage_users" },
  ].filter(item => !item.permission || hasPermission(item.permission));

  const userMenuItems = [
    { id: "account",  label: "My Account",   icon: User },
    { id: "messages", label: "My Messages",  icon: MessageSquare },
  ];

  const navigateTo = (pageId) => {
    navigate(`/${pageId}`);
    setSidebarOpen(false);
  };

  const handleLogout = async () => {
    try {
      await logout();
      toast.success("Logged out successfully");
    } catch {
      toast.error("Error logging out");
    }
  };

  const isActive = (id) => currentPage === id || location.pathname.startsWith(`/${id}`);

  return (
    <>
      {/* Mobile Sidebar Overlay */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside className={`fixed top-0 left-0 z-50 h-screen transition-all duration-300 ease-in-out bg-white border-r border-gray-200 shadow-lg ${
        sidebarOpen ? "translate-x-0" : "-translate-x-full"
      } lg:translate-x-0 ${sidebarCollapsed ? "w-16" : "w-64"}`}>

        {/* Header */}
        <div className="flex items-center justify-between h-16 px-4 border-b border-gray-200 bg-white">
          {!sidebarCollapsed && (
            <div className="flex items-center">
              <img src={logo} alt="Fortress Asset Managers Logo" className="h-6 w-6" />
              <span className="ml-3 text-sm font-bold text-gray-900">Fortress Asset Managers</span>
            </div>
          )}
          {sidebarCollapsed && (
            <img src={logo} alt="Fortress Asset Managers Logo" className="h-8 w-8 mx-auto" />
          )}
          <button
            onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
            className="hidden lg:flex items-center justify-center w-8 h-8 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-md transition-colors"
          >
            {sidebarCollapsed ? <ChevronRight size={16} /> : <ChevronLeft size={16} />}
          </button>
        </div>

        {/* Navigation */}
        <nav className="flex-1 px-3 py-4 space-y-1">
          {navigationItems.map((item) => {
            const Icon = item.icon;
            const active = isActive(item.id);
            return (
              <button
                key={item.id}
                onClick={() => navigateTo(item.id)}
                className={`group flex items-center w-full px-3 py-2 text-sm font-medium rounded-lg transition-all duration-200 ${
                  active
                    ? "bg-blue-50 text-blue-700 border-r-2 border-blue-700"
                    : "text-gray-700 hover:text-gray-900 hover:bg-gray-50"
                }`}
                title={sidebarCollapsed ? item.label : ""}
              >
                <Icon
                  size={20}
                  className={`flex-shrink-0 ${active ? "text-blue-700" : "text-gray-400 group-hover:text-gray-500"}`}
                />
                {!sidebarCollapsed && <span className="ml-3 truncate">{item.label}</span>}
              </button>
            );
          })}
        </nav>

        {/* Footer */}
        <div className="px-3 py-4 border-t border-gray-200">
          {userMenuItems.map((item) => {
            const Icon = item.icon;
            return (
              <button
                key={item.id}
                onClick={() => navigateTo(item.id)}
                className="group flex items-center w-full px-3 py-2 mb-1 text-sm font-medium text-gray-700 hover:text-gray-900 hover:bg-gray-50 rounded-lg transition-colors"
                title={sidebarCollapsed ? item.label : ""}
              >
                <Icon size={20} className="flex-shrink-0 text-gray-400 group-hover:text-gray-500" />
                {!sidebarCollapsed && <span className="ml-3 truncate">{item.label}</span>}
              </button>
            );
          })}

          <button
            onClick={handleLogout}
            className="group flex items-center w-full px-3 py-2 text-sm font-medium text-red-600 hover:text-red-900 hover:bg-red-50 rounded-lg transition-colors"
            title={sidebarCollapsed ? "Logout" : ""}
          >
            <LogOut size={20} className="flex-shrink-0 text-red-500 group-hover:text-red-700" />
            {!sidebarCollapsed && <span className="ml-3 truncate">Logout</span>}
          </button>
        </div>
      </aside>
    </>
  );
};

export default SidebarNavigation;
